import React, { useState } from "react";
import PropTypes from 'prop-types';
import { PatternFormat } from "react-number-format";
import { Alert, InputAdornment, MenuItem } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import Input from "@/components/Tags/Input";
import SelectField from "@/components/Tags/Select";
import LoadingButton from "@/components/LoadingButton";
import axios from "@/lib/axios";
import Button from "@/components/Tags/Form/Button";

const defaultValues = {
    name: '',
    mobile_no: '',
    source: '',
    email: '',
    desired_location: '',
    message: '',
};

const NumberFormatCustom = React.forwardRef(function NumberFormatCustom(props, ref) {
    const { onChange, ...other } = props;
    return (
        <PatternFormat
            {...other}
            getInputRef={ref}
            onValueChange={(values, sourceInfo) => {
                if(values.value){
                    onChange(values.value);
                }
            }}
            format="### ### ####"
            allowEmptyFormatting mask="-"
        />
    );
});

NumberFormatCustom.propTypes = {
    name: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
};

export default function FranchiseUs({ sources }){
    const { handleSubmit, reset, setValue, setError, clearErrors, getValues, control, trigger, formState: { errors, isValid } } = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        defaultValues: defaultValues
     });

     const [selectedSource, setSelectedSource] = useState("");
     const handleChangeSource = (source) => {
        setSelectedSource(source);
    };

    const [loading, setLoading] = useState(false);
    const [alertStatus, setAlertStatus] = useState(null);
    const [alertMessage, setAlertMessage] = useState(null);

    const onSubmit = async (data) => {
        setLoading(true);
        await axios.get('/sanctum/csrf-cookie');
        await axios.post(process.env.API_URL+`/franchise_us`, data)
                .then(function (response) {
                    setAlertStatus(response.data.status);
                    setAlertMessage(response.data.message);
                })
                .catch(function (error) {
                    setAlertStatus(203);
                    setAlertMessage('Connection Failed. Try again.');
                });
        setLoading(false);
    };

    const onError = (data) => {
        console.log('Invalid Form!');
    };
    return(
        <div className='max-w-screen-xl mx-auto bg-white'>
            <div className='relative grid sm:grid-cols-2 grid-cols-1 sm:px-6 px-3 pt-4 pb-20'>
                <div className='px-4 pt-4'>
                    <div className='relative'>
                        <div className='absolute -bottom-4 h-1.5 w-32 bg-rapide-yellow'></div>
                        <h1 className='text-4xl'>Be <span className='font-semibold'> part of a rapidly growing P15 Billion Industry!</span></h1>
                    </div>
                    <p className='pt-12 leading-9 font-light'>
                    Your trusted auto maintenance service partner. Here at Rapidé, we are always on the lookout for our next great franchisees. Do you have what it takes to be part of a 15 billion industry?
                    </p>
                </div>
                <div className='px-4 pt-4'>
                    {alertStatus && (
                        <Alert severity={`${(alertStatus  == 200) ? 'success' : 'error'}`}>
                            {alertMessage}
                        </Alert>
                    )}
                    <form className="mt-2" onSubmit={handleSubmit(onSubmit, onError)}>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="name"
                                        name="name"
                                        label="Name"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        error={errors.name != null}
                                        helperText={errors.name && errors.name?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="name"
                            control={control}
                            rules={{
                                required: 'Please enter your name',
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="text"
                                        id="mobile_no"
                                        name="mobile_no"
                                        label="Mobile Number"
                                        variant="filled"
                                        size="small"
                                        error={errors.mobile_no != null}
                                        helperText={errors.mobile_no && errors.mobile_no?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start">+63</InputAdornment>,
                                            inputComponent: NumberFormatCustom,
                                        }}
                                    />
                                )
                            }}
                            name="mobile_no"
                            control={control}
                            rules={{
                                required: "Please enter your mobile number",
                                minLength: {
                                    value: 10,
                                    message: 'Invalid mobile number.'
                                },
                                maxLength: {
                                    value: 10,
                                    message: 'Invalid mobile number.'
                                },
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        fullWidth
                                        type="email"
                                        id="email"
                                        name="email"
                                        label="Email"
                                        variant="filled"
                                        size="small"
                                        error={errors.email != null}
                                        helperText={errors.email && errors.email?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                            startAdornment: <InputAdornment position="start"><EmailOutlinedIcon/></InputAdornment>,
                                        }}
                                    />
                                )
                            }}
                            name="email"
                            control={control}
                            rules={{
                                required: 'Please enter your email address',
                            }}
                        />
                        <div className="w-full pb-2">
                            <Controller
                                render={({
                                    field: { onChange, onBlur, value, name, ref },
                                    fieldState: { isTouched, isDirty, error },
                                }) => {
                                    return(
                                        <SelectField
                                            variant="filled"
                                            size="small"
                                            id="source"
                                            name="source"
                                            label="How did you find us?"
                                            placeholder='Select any'
                                            error={errors.source != null}
                                            helperText={errors.source && errors.source?.message}
                                            onChange={(e, attr) => {
                                                onChange(e.target.value);
                                                handleChangeSource(e.target.value);
                                            }}
                                            value={selectedSource}
                                        >
                                            <MenuItem disabled value="">Select any</MenuItem>
                                            {sources.map((source, index) => {
                                                return (
                                                    <MenuItem key={index} value={source.id}>{source.name}</MenuItem>
                                                )
                                            })}
                                        </SelectField>
                                    )
                                }}
                                name="source"
                                control={control}
                                rules={{ required: "Please select any source" }}
                            />
                        </div>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="desired_location"
                                        name="desired_location"
                                        label="Where do you plan to put up the franchise?"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        error={errors.desired_location != null}
                                        helperText={errors.desired_location && errors.desired_location?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="desired_location"
                            control={control}
                            rules={{
                                required: 'Please enter your desired location',
                            }}
                        />
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                                fieldState: { isTouched, isDirty, error },
                            }) => {
                                return(
                                    <Input
                                        className="pb-2"
                                        type="text"
                                        id="message"
                                        name="message"
                                        label="Message"
                                        variant="filled"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={4}
                                        error={errors.message != null}
                                        helperText={errors.message && errors.message?.message}
                                        onChange={(e) => {
                                            onChange(e);
                                        }}
                                        InputProps={{
                                            disableUnderline: true,
                                        }}
                                    />
                                )
                            }}
                            name="message"
                            control={control}
                            rules={{
                                required: 'Please enter your message',
                            }}
                        />
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-4">
                            <div className="md:col-start-2 col-start-1">
                                <Button type="submit" loading={loading} label="Submit" llabel="Submitting..." />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}